import CompetitionRestrictionSummary from "./CompetitionRestrictionSummary"
import React from "react"
import { Link } from "react-router-dom"
import { DrawStatus } from "./EventPresentation"
import { competitionPlaces } from "./mainListPlaces"
import { tenis10CategoryLabel } from "./tenis10Event"

const count = value => Number.isFinite(value) && value >= 0 ? value : "—"
const genderLabels = { female: "Women", male: "Men", mixt: "Mixed", mixed: "Mixed" }
const disabled = c => c.status === "DISABLED"

// `withoutFees`: the competitions the event's fee warning marks in red with `feesMark` (competitionFees.js).
export default function EventCompetitions({ event, hasMatchesFilter, toEvent, withoutFees = [], feesMark }) {
  const competitions = event.competitions || []
  const noFees = new Set(withoutFees.map(c => c._id))
  // Every competition stays visible: matched ones lead while a filter applies and disabled ones close the list.
  const rank = c => (disabled(c) ? 2 : 0) - (hasMatchesFilter && c.matchesFilters ? 1 : 0)
  const ordered = [...competitions].sort((a, b) => rank(a) - rank(b))
  const inactive = competitions.filter(disabled).length
  if (event.competitionsAvailable === false || !competitions.length) {
    return <span className="text-muted">{event.competitionsAvailable === false ? "Competitions unavailable" : "No competitions"}</span>
  }
  return <section aria-label={"Competitions for " + event.name}>
    <div className="event-competitions-heading"><strong>{competitions.length} competitions{inactive > 0 ? " · " + inactive + " disabled" : ""}</strong>
      <small className="text-muted">Main List places / capacity · WildCard places included</small></div>
    <div className="event-competition-grid">
      {ordered.map(c => {
        const extras = []
        const showWildCards = c.wildCardCount !== 0 || c.wildCards !== 0
        if (c.waitingListCount !== 0) extras.push("Waiting " + count(c.waitingListCount))
        if (c.preRegistrationListCount !== 0) extras.push("Pre " + count(c.preRegistrationListCount))
        const gender = c.restrictions?.gender
        const status = !["ACTIVE", "CREATED", "ACCEPTED", "CONFIRMED"].includes(c.status)
          ? c.status ? c.status.charAt(0) + c.status.slice(1).toLowerCase() : "Status unavailable" : ""
        const tone = disabled(c) ? " is-disabled" : c.status === "MAINTENANCE" ? " is-paused" : ""
        return <Link key={c._id} className={"event-competition" + (hasMatchesFilter && c.matchesFilters ? " is-match" : "") + tone}
          to={toEvent("/events/" + event._id + "/competitions/" + c._id + "/entries")}>
          <span className="event-competition-main">
            <span className="event-competition-identity">
              {/* A Tenis10 competition is its category, its level 4 only the model's default (register E35); its level's dot leads the
                  written level (sportya-design: a level is its dot and its name, never a dot alone). */}
              <strong>{c.tenis10Level ? <><span className={"event-competition-level tenis10-level-" + c.tenis10Level} aria-hidden="true" />{tenis10CategoryLabel(c)}</>
                : <>{c.level ?? "—"}{c.gameType === "singles" ? "S" : c.gameType === "doubles" ? "D" : ""}</>}
                {gender ? " · " + (genderLabels[gender] || gender) : ""}</strong>
              <small className="event-competition-meta">#{c.competitionId ?? "—"}</small>
            </span>
            <strong className="event-competition-entries">{count(competitionPlaces(c).taken)} / {count(c.mlMaxNumber)}</strong>
          </span>
          <small className="event-competition-restrictions event-competition-meta"><CompetitionRestrictionSummary restrictions={c.restrictions} omitGender /></small>
          <span className="event-competition-secondary">
            <DrawStatus competition={c} compact />
            {showWildCards && <small className="event-competition-wc event-competition-meta">WC {count(c.wildCardCount)}/{count(c.wildCards)}</small>}
          </span>
          {(status || extras.length > 0) && <small className="event-competition-meta">{[status, ...extras].filter(Boolean).join(" · ")}</small>}
          {hasMatchesFilter && c.matchesFilters && <small>Matches filters</small>}
          {noFees.has(c._id) && <small className="text-danger event-competition-fees">{feesMark}</small>}
        </Link>
      })}
    </div>
  </section>
}
